import type {
  ImportOrderPhase13FormValues,
  ImportOrderPhase13LineForm,
} from "../ui/ImportOrderPhase13Form";
import {
  isMatchedImportLineForm,
  normalizeImportLineForm,
} from "./import-order-line-form";

export const IMPORT_ORDER_PHASE13_PREFILL_KEY =
  "sunlower:import-order-phase13-prefill";

export const IMPORT_ORDER_PHASE13_DRAFT_KEY =
  "sunlower:import-order-phase13-draft";

export type ImportOrderPhase13Draft = {
  returnTo: string;
  values: ImportOrderPhase13FormValues;
};

export function saveImportOrderPhase13Prefill(
  lines: ImportOrderPhase13LineForm[],
): void {
  sessionStorage.setItem(
    IMPORT_ORDER_PHASE13_PREFILL_KEY,
    JSON.stringify(lines),
  );
}

export function consumeImportOrderPhase13Prefill(): ImportOrderPhase13LineForm[] | null {
  const raw = sessionStorage.getItem(IMPORT_ORDER_PHASE13_PREFILL_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(IMPORT_ORDER_PHASE13_PREFILL_KEY);
  try {
    const parsed = JSON.parse(raw) as ImportOrderPhase13LineForm[];
    if (!Array.isArray(parsed)) return null;
    return parsed
      .map((line) => {
        try {
          return normalizeImportLineForm(line);
        } catch {
          return null;
        }
      })
      .filter((line): line is ImportOrderPhase13LineForm => {
        if (!line) return false;
        if (isMatchedImportLineForm(line)) {
          return line.orderedQuantity > 0;
        }
        return line.orderedQuantity > 0;
      });
  } catch {
    return null;
  }
}

export function saveImportOrderPhase13Draft(draft: ImportOrderPhase13Draft): void {
  sessionStorage.setItem(IMPORT_ORDER_PHASE13_DRAFT_KEY, JSON.stringify(draft));
}

export function peekImportOrderPhase13Draft(): ImportOrderPhase13Draft | null {
  const raw = sessionStorage.getItem(IMPORT_ORDER_PHASE13_DRAFT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ImportOrderPhase13Draft;
    if (
      !parsed ||
      typeof parsed.returnTo !== "string" ||
      !parsed.values ||
      !Array.isArray(parsed.values.lines)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function consumeImportOrderPhase13Draft(): ImportOrderPhase13Draft | null {
  const draft = peekImportOrderPhase13Draft();
  if (!draft) return null;
  sessionStorage.removeItem(IMPORT_ORDER_PHASE13_DRAFT_KEY);
  return draft;
}

export function appendLineToImportOrderDraft(
  line: ImportOrderPhase13LineForm,
): ImportOrderPhase13Draft | null {
  const draft = peekImportOrderPhase13Draft();
  if (!draft) return null;

  const existing = draft.values.lines.find(
    (l) => isMatchedImportLineForm(l) && isMatchedImportLineForm(line) && l.variantId === line.variantId,
  );
  const nextLines = existing && isMatchedImportLineForm(existing) && isMatchedImportLineForm(line)
    ? draft.values.lines.map((l) =>
        isMatchedImportLineForm(l) && l.variantId === line.variantId
          ? { ...l, orderedQuantity: l.orderedQuantity + line.orderedQuantity }
          : l,
      )
    : [...draft.values.lines, normalizeImportLineForm(line)];

  const next: ImportOrderPhase13Draft = {
    ...draft,
    values: { ...draft.values, lines: nextLines },
  };
  saveImportOrderPhase13Draft(next);
  return next;
}

export function consumeImportOrderPhase13DraftForReturnTo(
  returnTo: string,
): ImportOrderPhase13FormValues | null {
  const draft = consumeImportOrderPhase13Draft();
  if (!draft || draft.returnTo !== returnTo) {
    if (draft) {
      saveImportOrderPhase13Draft(draft);
    }
    return null;
  }
  return draft.values;
}
